import { HelpCircle } from 'lucide-react';
import type { JobStatus } from '@atlas/shared';
import { jobErrorTooltip } from '../lib/jobErrors.js';

const STATUS_CONFIG: Record<
  JobStatus,
  { label: string; dot: string; text: string; bg: string }
> = {
  queued: {
    label: 'Queued',
    dot: 'bg-[#aeaaa4]',
    text: 'text-[#73726e]',
    bg: 'bg-[#f1f1ef]',
  },
  running: {
    label: 'Transferring',
    dot: 'bg-[#2383e2]',
    text: 'text-[#2383e2]',
    bg: 'bg-[#e8f0fb]',
  },
  extracting: {
    label: 'Extracting',
    dot: 'bg-[#d9730d] animate-pulse',
    text: 'text-[#d9730d]',
    bg: 'bg-[#fef3e5]',
  },
  decrypt_moving: {
    label: 'Moving to virtual machine',
    dot: 'bg-[#9065b0] animate-pulse',
    text: 'text-[#9065b0]',
    bg: 'bg-[#f4f0f8]',
  },
  decrypt_extracting: {
    label: 'Getting keys',
    dot: 'bg-[#9065b0] animate-pulse',
    text: 'text-[#9065b0]',
    bg: 'bg-[#f4f0f8]',
  },
  decrypt_restoring: {
    label: 'Decrypting',
    dot: 'bg-[#9065b0] animate-pulse',
    text: 'text-[#9065b0]',
    bg: 'bg-[#f4f0f8]',
  },
  decrypt_exporting: {
    label: 'Moving back',
    dot: 'bg-[#9065b0] animate-pulse',
    text: 'text-[#9065b0]',
    bg: 'bg-[#f4f0f8]',
  },
  awaiting_decrypt: {
    label: 'Awaiting decrypt',
    dot: 'bg-[#d9730d]',
    text: 'text-[#d9730d]',
    bg: 'bg-[#fef3e5]',
  },
  complete: {
    label: 'Complete',
    dot: 'bg-[#0f7b6c]',
    text: 'text-[#0f7b6c]',
    bg: 'bg-[#e5f5f2]',
  },
  failed: {
    label: 'Failed',
    dot: 'bg-[#e03e3e]',
    text: 'text-[#e03e3e]',
    bg: 'bg-[#fbe8e8]',
  },
  cancelled: {
    label: 'Cancelled',
    dot: 'bg-[#9b9a97]',
    text: 'text-[#73726e]',
    bg: 'bg-[#f1f1ef]',
  },
  deleting: {
    label: 'Deleting',
    dot: 'bg-[#e03e3e] animate-pulse',
    text: 'text-[#e03e3e]',
    bg: 'bg-[#fbe8e8]',
  },
  deleted: {
    label: 'Deleted',
    dot: 'bg-[#9b9a97]',
    text: 'text-[#73726e]',
    bg: 'bg-[#f1f1ef]',
  },
  delete_failed: {
    label: 'Delete failed',
    dot: 'bg-[#e03e3e]',
    text: 'text-[#e03e3e]',
    bg: 'bg-[#fbe8e8]',
  },
};

export function StatusBadge({
  status,
  error,
}: {
  status: JobStatus;
  error?: string | null;
}) {
  const cfg = STATUS_CONFIG[status] ?? {
    label: status,
    dot: 'bg-[#aeaaa4]',
    text: 'text-[#73726e]',
    bg: 'bg-[#f1f1ef]',
  };
  // On a failed badge with a known reason, swap the leading dot for a trailing
  // help icon whose tooltip surfaces a plain-language explanation plus how to
  // resolve it (not just the raw code) on hover.
  const showHelp = (status === 'failed' || status === 'delete_failed') && !!error;
  const tooltip = jobErrorTooltip(error);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      {!showHelp && (
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      )}
      {cfg.label}
      {showHelp && (
        <span title={tooltip} className="inline-flex cursor-help">
          <HelpCircle size={12} className="shrink-0" />
        </span>
      )}
    </span>
  );
}
